import React, { useRef, useState, useEffect, useCallback } from 'react'
import {
  TextInput,
  TextInputRef,
  Pressable,
  ViewStyle,
  TextStyle,
} from '~/ui/primitives'
import styled, { css, useTheme } from '~/ui/styled'
import { CenterFlex } from '~components/El'

interface OTPInputProps {
  length?: number
  value?: string
  onChangeText?: (text: string) => void
  onFulfill?: (text: string) => void
  autoFocus?: boolean
  secureTextEntry?: boolean
  placeholder?: string
  placeholderChar?: string
  containerStyle?: ViewStyle
  cellStyle?: ViewStyle
  cellTextStyle?: TextStyle
  shakeOnInvalid?: boolean
  isInvalid?: boolean
}

const cleanDigits = (text: string, maxLen: number) =>
  text.replace(/[^0-9]/g, '').slice(0, maxLen)

const OTPInput: React.FC<OTPInputProps> = ({
  length = 6,
  value: controlledValue,
  onChangeText,
  onFulfill,
  autoFocus = true,
  secureTextEntry = false,
  placeholder = '',
  containerStyle,
  cellStyle,
  cellTextStyle,
  placeholderChar = '',
  shakeOnInvalid = true,
  isInvalid = false,
}) => {
  useTheme()
  const [value, setValue] = useState(controlledValue ?? '')
  const [focusedIndex, setFocusedIndex] = useState(-1)
  const [shaking, setShaking] = useState(false)
  const inputRef = useRef<TextInputRef>(null)

  useEffect(() => {
    if (controlledValue !== undefined) {
      setValue(controlledValue)
    }
  }, [controlledValue])

  useEffect(() => {
    if (!isInvalid || !shakeOnInvalid) return undefined
    setShaking(true)
    const id = window.setTimeout(() => setShaking(false), 320)
    return () => clearTimeout(id)
  }, [isInvalid, shakeOnInvalid])

  const focusInput = useCallback(() => {
    inputRef.current?.focus()
  }, [])

  /** 弹窗入场动画结束后再 focus，避免 H5 键盘无法弹出 */
  useEffect(() => {
    if (!autoFocus) return undefined
    const id = window.setTimeout(focusInput, 150)
    return () => clearTimeout(id)
  }, [autoFocus, focusInput])

  const handleChange = (text: string) => {
    const cleaned = cleanDigits(text, length)
    setValue(cleaned)
    setFocusedIndex(cleaned.length)
    onChangeText?.(cleaned)
    if (cleaned.length === length) onFulfill?.(cleaned)
  }

  const handleFocus = () => setFocusedIndex(value.length)
  const handleBlur = () => setFocusedIndex(-1)

  return (
    <Pressable onClick={focusInput} style={{ width: '100%' }}>
      <Container style={containerStyle} $shaking={shaking}>
        <Row style={{ pointerEvents: 'none' }}>
          {Array.from({ length }).map((_, i) => {
            const char = value[i]
            const show = char !== undefined
            const displayChar = secureTextEntry && show ? '•' : show ? char : placeholderChar
            const isFocused = focusedIndex === i

            return (
              <Cell
                key={i}
                $isFocused={isFocused}
                $isInvalid={isInvalid}
                style={cellStyle}
              >
                <CellText style={cellTextStyle}>
                  {displayChar || placeholder}
                </CellText>
              </Cell>
            )
          })}
        </Row>
        <OverlayInput
          ref={inputRef}
          value={value}
          onChangeText={handleChange}
          keyboardType="numeric"
          autoComplete="one-time-code"
          textContentType="oneTimeCode"
          maxLength={length}
          onFocus={handleFocus}
          onBlur={handleBlur}
          autoCorrect={false}
          aria-label="验证码输入"
        />
      </Container>
    </Pressable>
  )
}

export default OTPInput

const shakeKeyframes = css`
  @keyframes otp-shake {
    0%,
    100% {
      transform: translateX(0);
    }
    20% {
      transform: translateX(-8px);
    }
    40% {
      transform: translateX(8px);
    }
    60% {
      transform: translateX(-6px);
    }
    80% {
      transform: translateX(6px);
    }
  }
`

const Container = styled.View<{ $shaking: boolean }>`
  width: 100%;
  align-items: center;
  justify-content: center;
  position: relative;
  min-height: 48px;
  ${shakeKeyframes}
  animation: ${({ $shaking }) => ($shaking ? 'otp-shake 0.32s ease' : 'none')};
`

const Row = styled(CenterFlex)`
gap: 8px;
`

const Cell = styled.div<{ $isFocused: boolean; $isInvalid: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  width: 48px;
  height: 48px;
  border-radius: ${({ theme }) => theme.radii.lg};
  border-width: 1.5px;
  border-style: solid;
  border-color: ${({ $isFocused, $isInvalid, theme }) => {
    if ($isInvalid) return theme.colors?.error
    if ($isFocused) return theme.colors?.primary
    return theme.colors?.border
  }};
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors?.blockBackground || '#fff'};
  box-shadow: ${({ $isFocused, $isInvalid }) => {
    if ($isInvalid) return '0 0 0 3px rgba(250, 82, 81, 0.12)'
    if ($isFocused) return '0 0 0 3px rgba(7, 193, 96, 0.18)'
    return '0 1px 4px rgba(0, 0, 0, 0.03)'
  }};
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
`

const CellText = styled.Text`
  font-size: ${({ theme }) => theme.fontSizes['2xl'] || '20px'};
  color: ${({ theme }) => theme.colors?.text};
`

/** 透明输入层盖在格子上，单 input 承接键盘与粘贴 */
const OverlayInput = styled(TextInput).attrs({
  inputMode: 'numeric',
})`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  border: none;
  outline: none;
  background: transparent;
  opacity: 0;
  color: transparent;
  caret-color: transparent;
  font-size: 16px;
  cursor: text;
`
